import { AgentOSSection } from "@/components/AgentOSSection";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { Nebula } from "@/components/Nebula";
import { CosmicCanvas } from "@/components/CosmicCanvas";
import { DataManifestBanner } from "@/components/DataManifestBanner";
import { CaseStudiesSection } from "@/components/CaseStudiesSection";
import { VoicesMarqueeSection } from "@/components/VoicesMarqueeSection";
import { FinalCtaSection } from "@/components/FinalCtaSection";
import { useSubAppNavigation } from "@/hooks/use-sub-app-nav";
import { MEGA_MENU_EXTERNAL_URLS } from "@/i18n/menu-external-urls";
import { useT } from "@/lib/i18n";
import { getToken } from "@/lib/utils/auth-storage";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useRef, useState } from "react";

interface HomePageProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  onScrollToFooter?: () => void;
  onHideFooter?: () => void;
  /** 请求弹出登录弹窗，传入 'toolbox' 表示登录后跳转 toolbox */
  onRequestLogin?: (redirect?: "toolbox") => void;
}

function SectionHeader({ kicker, title, sub }: { kicker?: string; title: string; sub?: string }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-16">
      {kicker && <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">{kicker}</p>}
      <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">{title}</h2>
      {sub && <p className="mt-6 text-base text-muted-foreground/90 leading-relaxed">{sub}</p>}
    </div>
  );
}

const HomePage: React.FC<HomePageProps> = ({ onRequestLogin }) => {
  const t = useT();
  const [formOpen, setFormOpen] = useState(false);
  const { navigating: isNavigatingToToolbox, navigate } = useSubAppNavigation();

  // 3D 鼠标微视差与探照光斑
  const heroRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { stiffness: 50, damping: 25 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 50, damping: 25 });
  const [spotlightPos, setSpotlightPos] = useState({ x: 50, y: 40 });
  const [isHoveringHero, setIsHoveringHero] = useState(false);

  const handleHeroMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const xRel = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 ~ 0.5
    const yRel = (e.clientY - rect.top) / rect.height - 0.5; // -0.5 ~ 0.5
    mouseX.set(xRel);
    mouseY.set(yRel);
    setSpotlightPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  // 滚动驱动视差（Scroll-driven Parallax）
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // 背景层：微弱反向位移 + 滚动放大与下沉
  const bgX = useTransform(smoothMouseX, [-0.5, 0.5], [14, -14]);
  const bgY = useTransform(smoothMouseY, [-0.5, 0.5], [10, -10]);
  const bgScrollY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  // 前景内容层：文字保持稳定居中，仅在向下滚动时平滑淡出
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentScale = useTransform(scrollYProgress, [0, 0.7], [1, 0.94]);
  const contentScrollY = useTransform(scrollYProgress, [0, 0.7], [0, -40]);

  const handleExploreProducts = async () => {
    // GA4 埋点：立即体验按钮点击
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'homepage',
        event_label: 'try_now',
        button_text: 'Try Now / 立即体验'
      });
    }

    const token = getToken();
    if (!token) {
      // 未登录：弹出登录框，登录成功后自动跳转 toolbox
      onRequestLogin?.("toolbox");
      return;
    }
    // 已登录：跳转 toolbox（shared 模式直接跳，oauth2 模式先获取 code 再跳）
    await navigate('toolbox');
  };

  const engines = [
    {
      name: "OranHub",
      tag: t("Insight & Strategy", "洞察决策"),
      line: t("Read the market. Build a plan that works.", "读懂市场，拿到一份真正能落地的计划。"),
      href: MEGA_MENU_EXTERNAL_URLS["menu.prod.insights"],
    },
    {
      name: "OranSim",
      tag: t("Simulation & Foresight", "预测引擎"),
      line: t(
        "Test campaigns in a parallel universe — before you spend.",
        "在花钱之前，先在平行世界里把整场营销跑一遍。",
      ),
      href: MEGA_MENU_EXTERNAL_URLS["menu.prod.strategy"],
    },
    {
      name: "OranGen",
      tag: t("Content Factory", "内容工厂"),
      line: t(
        "Turn one winning idea into 30+ ready-to-publish creatives.",
        "一个最优创意，裂变成 30+ 条可直接发布的内容。",
      ),
      href: MEGA_MENU_EXTERNAL_URLS["menu.prod.contentFactory"],
    },
    {
      name: "OranMed",
      tag: t("Distributed Media", "分布式媒介"),
      line: t(
        "Distribute at scale — through owned accounts and real creators.",
        "通过自有账号和真实创作者，实现规模化分发。",
      ),
      // 待用户提供 OranMed 外链 — 暂留占位
    },
  ];

  return (
    <div className="landing-v2 min-h-screen text-foreground">
      {/* HERO */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseEnter={() => setIsHoveringHero(true)}
        onMouseLeave={() => {
          setIsHoveringHero(false);
          mouseX.set(0);
          mouseY.set(0);
        }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0b0907] pt-24 pb-16 will-change-transform"
      >
        {/* Layer 1: Background Visual Layer (.hero-space styled with screen blend mode & adaptive 66.67vw height) */}
        <motion.div
          className="absolute inset-0 pointer-events-none transition-opacity duration-700"
          style={{
            height: "max(100vh, 66.6667vw)",
            backgroundImage: `url('https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/8e6ac38326bd4a97aeccbd6e3b568efe.png')`,
            backgroundSize: "100% 100%",
            backgroundPosition: "center top",
            x: bgX,
            y: bgY,
            translateY: bgScrollY,
            scale: bgScale,
          }}
        />

        {/* Layer 1.5: 3D Deep Space Cosmos Engine */}
        <CosmicCanvas className="z-[1]" />

        {/* Layer 2: UI-Refined Ambient Vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              linear-gradient(180deg, rgba(11,9,7,0.18) 0%, rgba(11,9,7,0.04) 24%, rgba(11,9,7,0.12) 70%, rgba(11,9,7,0.75) 100%),
              radial-gradient(70% 62% at 50% 46%, transparent 28%, rgba(11,9,7,0.3) 100%)
            `,
          }}
        />

        {/* Layer 3: Dynamic Spotlight Glow (Follows Cursor) */}
        <div
          className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${
            isHoveringHero ? "opacity-100" : "opacity-40"
          }`}
          style={{
            background: `radial-gradient(700px circle at ${spotlightPos.x}% ${spotlightPos.y}%, rgba(249, 115, 22, 0.08), transparent 65%)`,
          }}
        />

        {/* Layer 4: Foreground Hero Content (.hero-inner: max-width 1080px with clamp gap) */}
        <motion.div
          className="relative z-10 text-center mx-auto flex flex-col items-center"
          style={{
            width: "min(100% - 40px, 1080px)",
            rowGap: "clamp(22px, 2.8vw, 36px)",
            translateY: contentScrollY,
            opacity: contentOpacity,
            scale: contentScale,
          }}
        >
          {/* Top Announcement Tag: UI Dual-Pill Badge */}
          <motion.div
            initial={{ opacity: 0, y: -16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <a
              href="https://whitebook.photog.art/background"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-stretch font-mono text-xs text-[#FDBA74] transition-all duration-300 group hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="flex items-center gap-1.5 px-4 py-1.5 rounded-l-full bg-[#F97316]/12 border border-[#F97316]/50 text-[#FDBA74] font-medium tracking-wider">
                ● NEW
              </span>
              <span className="flex items-center gap-1.5 px-4 py-1.5 rounded-r-full border border-l-0 border-[#F97316]/50 bg-black/40 group-hover:bg-[#F97316]/15 text-[#FDBA74] font-medium transition-colors">
                {t("Whitepaper is Live › Download", "白皮书已上线 › 下载")}
              </span>
            </a>
          </motion.div>

          {/* Main Headline: Exactly matched to UI Material Typography (clamp 42px~92px, line-height 1.12, letter-spacing -0.035em) */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="w-full"
          >
            <h1
              className="font-black tracking-[-0.035em] text-[#F2ECE5] leading-[1.12] mx-auto text-balance"
              style={{
                fontSize: "clamp(42px, 6.25vw, 92px)",
              }}
            >
              <span>{t("Swarm Intelligence-Driven", "群体智能驱动型")}</span>
              <br />
              <span
                className="inline-block pb-2 pt-0.5 bg-clip-text text-transparent font-black"
                style={{
                  backgroundImage: "linear-gradient(180deg, #FDBA74 0%, #F97316 70%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("Social Media Marketing AgentOS", "社媒营销 AgentOS")}
              </span>
            </h1>
          </motion.div>

          {/* Subtitle: Exactly matched to UI Material (clamp 15px~19px, line-height 1.6, max-width 42ch) */}
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="text-[#F2ECE5]/75 font-normal tracking-[0.04em] leading-[1.6] mx-auto"
            style={{
              fontSize: "clamp(15px, 1.6vw, 19px)",
              maxWidth: "42ch",
            }}
          >
            {t("Building the Next-Generation Marketing Tech Stack", "构建下一代营销技术栈")}
          </motion.p>

          {/* Dual CTA Buttons: Synchronized Crisp Snap-Rise Entrance */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 pt-1 animate-cta-snap-rise will-change-transform">
            {/* Primary CTA: 立即体验 (Pulse Halo + Hover Sheen) */}
            <div className="relative group">
              {/* Ambient Breathing Halo */}
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#F97316] to-[#FDBA74] opacity-40 blur-md group-hover:opacity-75 group-hover:blur-lg transition-all duration-500 animate-pulse pointer-events-none" />
              
              <motion.button
                onClick={handleExploreProducts}
                disabled={isNavigatingToToolbox}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 22 }}
                className="relative inline-flex items-center justify-center gap-2 px-9 py-3.5 sm:py-4 rounded-full bg-gradient-to-b from-[#FDBA74] to-[#F97316] hover:from-[#FED7AA] hover:to-[#FB923C] text-[#180C02] font-bold text-base transition-all duration-200 shadow-[0_0_32px_rgba(249,115,22,0.45)] hover:shadow-[0_0_48px_rgba(249,115,22,0.7)] overflow-hidden"
              >
                {/* Diagonal Light Sheen Sweep */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/35 to-transparent pointer-events-none" />

                {isNavigatingToToolbox ? (
                  t("Redirecting…", "跳转中…")
                ) : (
                  <>
                    <span className="relative z-10">{t("Try Now", "立即体验")}</span>
                    <span className="relative z-10 font-semibold text-lg leading-none transition-transform duration-300 group-hover:translate-x-1.5">›</span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Secondary CTA: 联系我们 (Glass Sheen) */}
            <div>
              <motion.button
                onClick={() => setFormOpen(true)}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 22 }}
                className="group relative inline-flex items-center justify-center px-9 py-3.5 sm:py-4 rounded-full bg-white/[0.04] hover:bg-[#F97316]/10 text-[#F2ECE5] hover:text-[#FDBA74] font-medium text-base border border-white/20 hover:border-[#F97316]/60 backdrop-blur-md transition-all duration-300 shadow-[0_0_0_transparent] hover:shadow-[0_0_24px_rgba(249,115,22,0.25)] overflow-hidden"
              >
                {/* Subtle Glass Sheen Sweep */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />

                <span className="relative z-10">{t("Contact us", "联系我们")}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* SECTION 2 · AGENTOS ECOSYSTEM BASED ON ORANSIM */}
      <AgentOSSection
        t={t}
        onRequestLogin={onRequestLogin}
        onExploreProducts={handleExploreProducts}
        isNavigating={isNavigatingToToolbox}
      />

      {/* S3b · REALTIME METRICS MANIFEST + CLIENT LOGO TICKER */}
      <DataManifestBanner />

      {/* S4 · CASES（案例优先） */}
      <CaseStudiesSection />

      {/* S4b · VOICES（客户与创作者的声音 · 跑马灯） */}
      <VoicesMarqueeSection />

      

      {/* S5 · CTA（深色 · 橙色线条星球 + 动态打字机） */}
      <FinalCtaSection
        onContactClick={() => setFormOpen(true)}
        onExploreProducts={handleExploreProducts}
        onDiagnosisSubmit={(_brand) => setFormOpen(true)}
        isNavigating={isNavigatingToToolbox}
      />

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default HomePage;
